import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { AbilityServiceSignal } from '@casl/angular';
import { Store } from '@ngxs/store';
import { ActivitiesPipesModule } from '@wilson/activities/pipes';
import { AuthorizationModule } from '@wilson/authorization';
import { FeatureFlagsModule } from '@wilson/feature-flags';
import {
  AccurateDateIntervalWithShift,
  AccurateTimeDetails,
  ActivityRow,
  Label,
  OperationStatus,
  Project,
  RoleAction,
  RolePermissionSubject,
  SHIFT_WIDTH_ADJUSTMENT_FOR_ACTIVITIES_PX,
  ShiftColorOptions,
  StaffLayoutStyle,
} from '@wilson/interfaces';
import { LabelsState } from '@wilson/labels';
import { IGNORE_ON_CLICK_CLASS_NAME } from '@wilson/non-domain-specific/ng-zorro/nz-drawer/core';
import { ProjectsState } from '@wilson/projects/state';
import {
  AssignmentShiftCardComponent,
  dateToPositionPx,
  DeferredAssignmentShiftCardHelperService,
  GanttEndingPixelPipe,
  GanttStartingPixelPipe,
  SelectedItemOutput,
  ShiftCardTooltipStateService,
  ShiftMultiSelectHelperService,
  ShiftsIconRenderingHelperService,
  TimeDifferenceWidthPxPipe,
} from '@wilson/shift-timeline/assignments-gantt-chart';
import {
  GanttShiftEndTimePipe,
  GanttShiftStartTimePipe,
  RowsToActivitiesPipe,
  ShiftTimelinePipesModule,
} from '@wilson/shift-timeline/pipes';
import {
  DragAndDropModeService,
  PreferredSettingsStateFacadeService,
  determineShiftRenderDatetime,
} from '@wilson/shift-timeline/services';
import {
  ShiftTimelineDataState,
  ShiftTimelineSettingsState,
  ShiftTimelineState,
} from '@wilson/shift-timeline/state';
import { differenceInMilliseconds } from 'date-fns';
import { Observable, Subscription, combineLatest, map, of } from 'rxjs';
import { LoadingShiftCardComponent } from '../../components/loading-shift-card/loading-shift-card.component';
import { TimelineRowDragHandlerV2Directive } from '../../components/timeline-directives/timeline-row-drag-handler-v2.directive';
import { GanttActivityDragHandlerHelperService } from '../../services/gantt-activity-drag-handler-helper.service';
import { GanttActivityResizeHandlerHelperService } from '../../services/gantt-activity-resize-handler-helper.service';
import { TimelineShiftDragAndDropDirectiveHelperService } from '../../services/timeline-shift-drag-and-drop-directive-helper.service';

@Component({
  selector: 'wilson-timeline-auto-size-user-shifts-v2',
  providers: [
    ShiftCardTooltipStateService,
    TimelineShiftDragAndDropDirectiveHelperService,
    GanttShiftStartTimePipe,
    GanttShiftEndTimePipe,
    GanttStartingPixelPipe,
    GanttEndingPixelPipe,
  ],
  templateUrl: './timeline-auto-size-user-shifts-v2.component.html',
  styleUrls: ['./timeline-auto-size-user-shifts-v2.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ShiftTimelinePipesModule,
    TimelineRowDragHandlerV2Directive,
    FeatureFlagsModule,
    AssignmentShiftCardComponent,
    ActivitiesPipesModule,
    LoadingShiftCardComponent,
    TimeDifferenceWidthPxPipe,
    AuthorizationModule,
    RowsToActivitiesPipe,
  ],
})
export class TimelineAutoSizeUserShiftsV2Component
  implements OnInit, OnChanges, AfterViewInit, OnDestroy
{
  private readonly store = inject(Store);
  readonly dragAndDropModeService = inject(DragAndDropModeService);
  readonly shiftCardTooltipStateService = inject(ShiftCardTooltipStateService);
  readonly timelineShiftDragAndDropDirectiveHelperService = inject(
    TimelineShiftDragAndDropDirectiveHelperService,
  );
  readonly ganttActivityDragHandlerHelperService = inject(
    GanttActivityDragHandlerHelperService,
  );
  readonly ganttActivityResizeHandlerHelperService = inject(
    GanttActivityResizeHandlerHelperService,
  );
  readonly deferredAssignmentShiftCardHelperService = inject(
    DeferredAssignmentShiftCardHelperService,
  );
  private readonly shiftMultiSelectHelperService = inject(
    ShiftMultiSelectHelperService,
  );
  private readonly shiftsIconRenderingHelperService = inject(
    ShiftsIconRenderingHelperService,
  );
  private readonly preferredSettingsStateFacadeService = inject(
    PreferredSettingsStateFacadeService,
  );

  readonly SHIFT_WIDTH_ADJUSTMENT_FOR_ACTIVITIES_PX =
    SHIFT_WIDTH_ADJUSTMENT_FOR_ACTIVITIES_PX;
  @Input() shiftView!: AccurateDateIntervalWithShift;
  @Input() userId!: string;
  @Input() showShiftValidation = false;
  @Input() draggingItemId!: string | null;
  @Input() updatingShiftIds!: string[] | null;
  @Input() isCursorGrab = false;
  @Input() isActivityDraggable = false;
  @Input() numberOfInnerRows = 0;
  @Input() innerRowIndex = 0;
  @Input() shiftHeight!: number;
  @Input() isLastShiftInLastUserRow = false;

  @Output() selectShiftEvent = new EventEmitter<SelectedItemOutput>();
  @Output() selectActivityEvent = new EventEmitter<SelectedItemOutput>();

  protected RoleAction = RoleAction;
  protected RolePermissionSubject = RolePermissionSubject;
  protected IGNORE_ON_CLICK_CLASS_NAME = IGNORE_ON_CLICK_CLASS_NAME;
  leftPixelPosition$!: Observable<number>;
  rightPixelPosition$!: Observable<number>;
  shiftStartDetail!: AccurateTimeDetails;
  shiftEndDetail!: AccurateTimeDetails;
  OperationStatus = OperationStatus;
  shiftIdsToHighlight$ = this.store.select(
    ShiftTimelineDataState.shiftIdsToHighlight,
  );
  timelineWidthPixel$ = this.store.select(ShiftTimelineState.frameLength);
  interval$ = this.store.select(ShiftTimelineDataState.timeframe);
  determinedShiftActivities$!: Observable<ActivityRow[]>;
  areActivitiesVisible$ = this.store.select(
    ShiftTimelineSettingsState.areActivitiesVisible,
  );
  staffLayoutStyle$!: Observable<StaffLayoutStyle>;
  StaffLayoutStyle = StaffLayoutStyle;
  protected can = inject(AbilityServiceSignal).can;
  public shiftColorOption$!: Observable<ShiftColorOptions>;

  private subscriptions = new Subscription();

  protected loadIconsImmediately$!: Observable<boolean>;
  protected project$!: Observable<Project | null | undefined>;
  protected label$!: Observable<Label | null | undefined>;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['shiftView']) {
      this.shiftCardTooltipStateService.initState(
        this.userId,
        this.shiftView.shift,
      );

      const shiftRenderDatetime = determineShiftRenderDatetime(
        this.shiftView.shift,
      );
      this.shiftStartDetail = shiftRenderDatetime.startDatetime;
      this.shiftEndDetail = shiftRenderDatetime.endDatetime;

      this.project$ = this.shiftView.shift.projectId
        ? this.store.select(
            ProjectsState.projectById(this.shiftView.shift.projectId),
          )
        : of(null);

      this.label$ = this.shiftView.shift.labels?.length
        ? this.store.select(
            LabelsState.getLabelById(this.shiftView.shift.labels[0].id),
          )
        : of(null);
    }
  }

  ngAfterViewInit(): void {
    if (this.isLastShiftInLastUserRow) {
      this.shiftsIconRenderingHelperService.markLastShiftAsRenderedForTimeline();
    }
  }

  ngOnInit(): void {
    this.loadIconsImmediately$ =
      this.shiftsIconRenderingHelperService.isLastShiftRenderedInTimeline$;
    this.shiftColorOption$ =
      this.preferredSettingsStateFacadeService.shiftColorOptions$;
    this.staffLayoutStyle$ =
      this.preferredSettingsStateFacadeService.staffLayoutStyle$;

    this.leftPixelPosition$ = combineLatest([
      this.store.select(ShiftTimelineState.frameLength),
      this.store.select(ShiftTimelineDataState.timeframe),
    ]).pipe(
      map(([frameLength, timeframe]) => {
        return dateToPositionPx(new Date(this.shiftStartDetail.date), {
          fromRightSide: false,
          timelineWidthPixel: frameLength,
          interval: timeframe,
        });
      }),
    );

    this.rightPixelPosition$ = combineLatest([
      this.store.select(ShiftTimelineState.frameLength),
      this.store.select(ShiftTimelineDataState.timeframe),
    ]).pipe(
      map(([frameLength, timeframe]) => {
        return dateToPositionPx(new Date(this.shiftEndDetail.date), {
          fromRightSide: true,
          timelineWidthPixel: frameLength,
          interval: timeframe,
        });
      }),
    );

    const shiftDuration = differenceInMilliseconds(
      this.shiftView.accurateStartDateTime,
      this.shiftView.accurateEndDateTime,
    );

    this.timelineShiftDragAndDropDirectiveHelperService.setShiftDuration(
      shiftDuration,
    );

    this.determinedShiftActivities$ = this.store.select(
      ShiftTimelineDataState.shiftDeterminedActivities(this.shiftView.shift.id),
    );
  }

  setPreviewRef(previewEle: HTMLElement): void {
    this.timelineShiftDragAndDropDirectiveHelperService.setShiftPreviewCard(
      previewEle,
    );
  }

  closeShiftDrawerOnDragOrResize(): void {
    this.shiftMultiSelectHelperService.clearSelection();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
